import React, { useRef, useState, useEffect, useMemo } from "react";
import { useFormContext } from 'react-hook-form'
import useDebounce from "../../../../utils/useDebounce";
import TextInput from "../../../../shared/Inputs/TextInput/TextInput";
import SelectInput from "../../../../shared/Inputs/SelectInput/SelectInput";
import FileInput from "../../../../shared/Inputs/FileInput/FileInput";
import { INSTANCE, UPLOADS_ENDPOINT } from "../../../../server-parameters";
import { copyContactPerson, yearsAgo } from "../../../../utils/helpers";
import { validateName, validateString, validateDate, smartValidateString } from "../../../../validate/validators";
import { Country, Lang } from "../../../../types/common-types";
import { State } from "../../../../state-actions-reducers";
import DatePicker from "../../../../shared/Inputs/DatePicker/DatePicker";
import { checkPhoneEmail } from "../../../../hooks/useCheckPhoneEmailCustomHook";
import styles from "../../FiscalScreenOne.module.scss";
import cn from 'classnames'
import warningImage from "../../../../images/warning.svg";
import RussianForm from "../RfInstance/Citizenships/RussianForm";
import KyrgyzstanForm from "../RfInstance/Citizenships/KyrgyzstanForm";
import RomanianForm from "../RfInstance/Citizenships/RomanianForm";
import ChinaForm from "../RfInstance/Citizenships/ChinaForm";
import KazakhstanForm from "../RfInstance/Citizenships/KazakhstanForm";
import BelarusForm from "../RfInstance/Citizenships/BelarusForm";
import UzbekistanForm from "../RfInstance/Citizenships/UzbekistanForm";
import VietnamForm from "../RfInstance/Citizenships/VietnamForm";
import AddressInputs from "../../AddressInputs/AddressInputs";
import {
  passportTypeKz,
  passportTypeRf,
  passportTypeRom,
  passportTypeKgz,
  passportTypeGer,
  passportTypeUsa,
  passportTypeTaj,
  passportTypeTur,
  passportTypeUzb,
  DocumentTypeValuesUdvAbroad,
  DocumentTypeValuesTypePersonalities,
  DocumentTypeValuesTypePassport,
} from "../../../../content/passportTypeFiscal";
import { stringDigitsLength } from "../../../../validate/validators";
import { soleTraderType } from "../../../../content/fiscalType";
import { getFiscalFullName } from "../../../../utils/TranslateFiscalKzt/getFiscalFullName";
import { getFiscalShortName } from "../../../../utils/TranslateFiscalKzt/getFiscalShortName";
import { getCertificateRegistrationNumber, getCertificateRegistrationNumberRf } from "../../../../utils/TranslateFiscalKgz/getCertificateRegistrationNumber";
import { getCertificateRegistrationDate } from "../../../../utils/TranslateFiscalKgz/getCertificateRegistrationDate";
import { getCertificateRegistrationImage } from "../../../../utils/TranslateFiscalKgz/getCertificateRegistrationImage";
import AddCitizenships from "../../../../components/AddCitizenships/AddCitizenships";
import { kzCity, rfCity } from "../../../../content/cities";
import InnExist from "../../../../components/IdentityExist/InnExist";
import { inclineFirstname, inclineLastname, inclineMiddlename } from 'lvovich';
import InfoMessage from "../../../../shared/InfoMessage/InfoMessage";

interface Props {
  lang: Lang;
  accountForm: any;
  citizenshipCountriesList: Country[];
  addressCountries: Country[];
  regionCityRegistered: any;
  regionCityFactual: any;
  regionCityResidence: any;
}

type CitizenshipFormProps = { lang: any; accountForm: any }

const CITIZENSHIP_PASSPORT_TYPE_MAP: Record<string, any> = {
  'Казахстан': passportTypeKz,
  'Российская Федерация': passportTypeRf,
  'Беларусь': passportTypeRf,
  'Кыргызстан': passportTypeKgz,
  'Румыния': passportTypeRom,
  'Германия': passportTypeGer,
  'Соединенные Штаты': passportTypeUsa,
  'Таджикистан': passportTypeTaj,
  'Турция': passportTypeTur,
  'Узбекистан': passportTypeUzb,
}

const CITIZENSHIP_FORM_MAP: Record<string, React.ComponentType<CitizenshipFormProps>> = {
  'Российская Федерация': RussianForm,
  'Китай': ChinaForm,
  'Казахстан': KazakhstanForm,
  'Кыргызстан': KyrgyzstanForm,
  'Румыния': RomanianForm,
  'Беларусь': BelarusForm,
  'Бразилия': BelarusForm,
  'Италия': BelarusForm,
  'Австралия': UzbekistanForm,
  'Азербайджан': UzbekistanForm,
  'Армения': UzbekistanForm,
  'Соединенное Королевство': UzbekistanForm,
  'Германия': UzbekistanForm,
  'Испания': UzbekistanForm,
  'Сирийская Арабская Республика': UzbekistanForm,
  'Соединенные Штаты': UzbekistanForm,
  'Таджикистан': UzbekistanForm,
  'Узбекистан': UzbekistanForm,
  'Франция': UzbekistanForm,
  'Швейцария': UzbekistanForm,
  'Болгария': VietnamForm,
  'Израиль': VietnamForm,
  'Исландия': VietnamForm,
  'Молдова, Республика': VietnamForm,
  'Нидерланды': VietnamForm,
  'Норвегия': VietnamForm,
  'Швеция': VietnamForm,
  'Южная Африка': VietnamForm,
  'Вьетнам': VietnamForm,
  'Грузия': VietnamForm,
  'Индонезия': VietnamForm,
  'Индия': VietnamForm,
  'Южная Корея': VietnamForm,
  'Финляндия': VietnamForm,
}

/* REACT COMPONENT */
function IpScreenOne({
  lang,
  accountForm,
  citizenshipCountriesList,
  addressCountries,
  regionCityRegistered,
  regionCityFactual,
  regionCityResidence
}: Props) {
  const { watch, setValue, getValues, formState: { errors } } = useFormContext<State>()
  const state = watch()
  const getCitizenshipFormat = (citizenship: string) => {
    return citizenshipCountriesList.find((country: Country) => {
      return country.value === citizenship;
    })?.label?.rus;
  };
  const selectedCitizenship = useMemo(() => {
    return getCitizenshipFormat(state.citizenship);
  }, [state.citizenship]);

  const [focus, setFocus] = useState("");
  const debouncedPhone = useDebounce(state.mobilePhoneNumbers[0], 1500);
  const debouncedMail = useDebounce(state.emails[0], 1500);

  async function phoneEmailMessage() {
    try {
      let response = await checkPhoneEmail(state, debouncedPhone, debouncedMail)
      if (response.message !== 'Invalid token') {
        setValue('isPhoneOk', response.isPhoneOk, { shouldDirty: true })
        setValue('isEmailOk', response.isEmailOk, { shouldDirty: true })
      } else {
        setValue('serverError', true, { shouldDirty: true });
        alert(accountForm.serverError[lang]);
      }
    } catch (err) {
      setValue('serverError', true, { shouldDirty: true });
      alert(accountForm.serverError[lang]);
    }
  }

  useEffect(() => {
    if (debouncedPhone && debouncedPhone.length > 2) {
      phoneEmailMessage();
    } else {
      setValue('isPhoneOk', true, { shouldDirty: true });
    }
  }, [debouncedPhone]);
  useEffect(() => {
    if (debouncedMail) {
      phoneEmailMessage();
    } else {
      setValue('isEmailOk', true, { shouldDirty: true });
    }
  }, [debouncedMail]);

  const fullNameObject = getFiscalFullName(state.F_FiscalType, state.countryRegistration);
  const shortNameObject = getFiscalShortName(state.F_FiscalType, state.countryRegistration);

  const [shortName, setShortName] = useState(shortNameObject);
  const [fullName, setFullName] = useState(fullNameObject);

  useEffect(() => {
    setShortName(shortNameObject);
    setFullName(fullNameObject);
  }, [state.F_FiscalType]);
  useEffect(() => {
    setValue('F_FiscalType', '1', { shouldDirty: true });
  }, [state.countryRegistration]);

  useEffect(() => {
    setValue('mainCity', INSTANCE === 'msk' ? rfCity[1].value : rfCity[0].value, { shouldDirty: true })
  }, [lang]);
  useEffect(() => {
    if (state.passportType?.length === 0) {
      setValue('passportType', selectedCitizenship === "Казахстан" ? String(passportTypeKz[0].value) : String(passportTypeRf[0].value), { shouldDirty: true });
    }
  }, [selectedCitizenship, state.F_FiscalType]);

  const certificateRegistrationNumber = getCertificateRegistrationNumberRf(state.F_FiscalType);
  const certificateRegistrationDate = getCertificateRegistrationDate(state.F_FiscalType);
  const certificateRegistrationImage = getCertificateRegistrationImage(state.F_FiscalType);
  const [showAddCitizenships, setShowAddCitizenships] = useState(false);

  const CitizenshipForm = selectedCitizenship ? CITIZENSHIP_FORM_MAP[selectedCitizenship] ?? null : null

  return (
    <>
      <div>
        <div className="form-group">
          <div className="row">
            <div className="col-3 col-xs-12">
              <TextInput
                testName={`F_INN`}
                testErrorName={`error-F_INN`}
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.F_INN}
                onChange={(F_INN) => {
                  setValue('F_INN', F_INN.replace(/\D/gim, "").substr(0, 12), { shouldDirty: true });
                }}
                // onChange={F_INN =>
                //   setValue('F_INN', F_INN, { shouldDirty: true })
                // }
                value={state.F_INN || ""}
                required
                valid={stringDigitsLength(state.F_INN, 12, false) && !errors.F_INN}
                mask={"999999999999"}
              />
            </div>
            <InnExist accountForm={accountForm} lang={lang}  show={!state.isIdentityOk} />
            <div className="col-3 col-xs-12">
              {/* certificateRegistrationNumber */}
              <TextInput
                testName={`certificateRegistrationNumber`}
                testErrorName={`error-certificateRegistrationNumber`}
                disabled={!state.agreed}
                lang={lang}
                content={certificateRegistrationNumber}
                onChange={(certificateRegistrationNumber) => {
                  setValue('certificateRegistrationNumber', certificateRegistrationNumber.replace(/\D/gim, ""), { shouldDirty: true });
                }}
                required
                value={state.certificateRegistrationNumber || ""}
                valid={!errors.certificateRegistrationNumber}
                mask={"99 999999999"}
              />
            </div>
            <div className="col-6 col-xs-12">
              <FileInput
                disabled={!state.agreed}
                lang={lang}
                url={UPLOADS_ENDPOINT}
                autorization={state.formId}
                target={state.documentsImages}
                content={accountForm.F_INN_Image_IP}
                
                onChange={F_INN_Image => {
                  setValue('F_INN_Image', [...(getValues('F_INN_Image')||[]), F_INN_Image], { shouldDirty: true })
                }}
                value={state.F_INN_Image[state.F_INN_Image.length - 1]}
                valid={!errors.F_INN_Image}
                documentType="INN"
                imageErrors={state.imageError}
              />
            </div>
            <div className="col-12 col-xs-12">
              {state.F_INN.length < 10 && <InfoMessage type={"default"} text={<>
                <span>{accountForm.organizationsAutoFill[lang]}</span>
              </>} />}
            </div>
            <div className="col-6 col-xs-12">
              {/* F_OGRNIP */}
              <TextInput
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.F_OGRNIP}
                onChange={(F_OGRNIP) => {
                  setValue('F_OGRNIP', F_OGRNIP.replace(/\D/gim, "").substr(0, 15), { shouldDirty: true });
                }}
                value={state.F_OGRNIP || ""}
                valid={stringDigitsLength(state.F_OGRNIP, 15, false) && !errors.F_OGRNIP}
                mask={"999999999999999"}
              />
            </div>
            <div className="col-6 col-xs-12">
              <FileInput
                disabled={!state.agreed}
                lang={lang}
                url={UPLOADS_ENDPOINT}
                autorization={state.formId}
                target={state.documentsImages}
                content={accountForm.F_OGRNIP_Image}
                
                onChange={F_OGRN_Image => {
                  setValue('F_OGRN_Image', [...(getValues('F_OGRN_Image')||[]), F_OGRN_Image], { shouldDirty: true })
                }}
                valid={!errors.F_OGRN_Image}
                documentType="OGRN"
                value={state.F_OGRN_Image[state.F_OGRN_Image.length - 1]}
                imageErrors={state.imageError}
              />
            </div>
            <div className="col-3 col-xs-12">
              <DatePicker
                lang={lang}
                accountForm={accountForm}
                accountFormKey="registeredDate"
                onChange={(data) => {
                  setValue('registeredDate', data, { shouldDirty: true });
                }}
                value={state.registeredDate}
                focus={focus === "registeredDate" || undefined}
                onBlur={() => setFocus("")}
                valid={!errors.registeredDate}
                required
                min={new Date(new Date().setFullYear(new Date().getFullYear() - 67))}
                max={new Date()}
              />
            </div>
            <div className="col-4 col-xs-12">
              <FileInput
                disabled={!state.agreed}
                lang={lang}
                url={UPLOADS_ENDPOINT}
                autorization={state.formId}
                target={state.documentsImages}
                content={accountForm.F_EGRIP_Image}
                
                onChange={(F_EGRUL_Image) => {
                  setValue('F_EGRUL_Image', [...(getValues('F_EGRUL_Image')||[]), F_EGRUL_Image], { shouldDirty: true });
                }}
                value={state.F_EGRUL_Image[state.F_EGRUL_Image.length - 1]}
                valid={!errors.F_EGRUL_Image}
                documentType="EGRYUL"
                imageErrors={state.imageError}
              />
            </div>
            <div className={cn("col-5 col-xs-12", styles.inputWidthCheckbox)}>
              <div className={cn(styles.checkInputBox, styles.absoluteInput)}>
              <input
                id="hideContractInput"
                name="hideContractInput"
                type="checkBox"
                onChange={() => {
                  setValue('noLeaseAgreement', !state.noLeaseAgreement, { shouldDirty: true })
                  setValue('F_Rent_Contract_Image', [], { shouldDirty: true })
                }}
                checked={state.noLeaseAgreement}
              />
              <label htmlFor="hideContractInput">
                {accountForm.noRentContractBtnDesc[lang]}
              </label>
            </div>
              <FileInput
                disabled={state.noLeaseAgreement}
                lang={lang}
                url={UPLOADS_ENDPOINT}
                autorization={state.formId}
                target={state.documentsImages}
                content={accountForm.F_Rent_Contract_Image}
                
                onChange={F_Rent_Contract_Image => {
                  setValue('F_Rent_Contract_Image', [...(getValues('F_Rent_Contract_Image')||[]), F_Rent_Contract_Image], { shouldDirty: true })
                }}
                valid={!errors.F_Rent_Contract_Image}
                documentType="Lease"
                value={
                  state.F_Rent_Contract_Image[
                    state.F_Rent_Contract_Image.length - 1
                  ]
                }
                imageErrors={state.imageError}
              />
            </div>
          </div>
        </div>
        <div className="form-group">
          <div className="row">
            <div className="col-12 col-xs-12">
              <h3>{accountForm.stepOnePersonalTitle[lang]}</h3>
            </div>
            <div className="col-4 col-xs-12">
              {/* LAST NAME */}
              <TextInput
                testName={"headLastname"}
                testErrorName={"error-headLastname"}
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.headLastnameIp}
                onChange={(headLastname) => {
                  setValue('headLastname', headLastname, { shouldDirty: true });
                  if (state.copyContactPerson)
                    setValue('contactPersonLastname', headLastname, { shouldDirty: true });
                  if (lang === "eng") {
                    setValue('headLastnameGenitive', headLastname, { shouldDirty: true });
                  } else {
                    setValue('headLastnameGenitive', inclineLastname(headLastname, 'genitive'), { shouldDirty: true })
                  }
                }}
                value={state.headLastname || ""}
                required
                valid={state.headLastname.length > 0 ? smartValidateString(state.headLastname) && !errors.headLastname : !errors.headLastname}
              />
            </div>
            <div className="col-4 col-xs-12">
              {/* FIRST NAME */}
              <TextInput
                testName={"headFirstname"}
                testErrorName={"error-headFirstname"}
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.headFirstnameIp}
                onChange={(headFirstname) => {
                  setValue('headFirstname', headFirstname, { shouldDirty: true });
                  if (state.copyContactPerson)
                    setValue('contactPersonFirstname', headFirstname, { shouldDirty: true });
                  if (lang === "eng") {
                    setValue('headFirstnameGenitive', headFirstname, { shouldDirty: true });
                  } else {
                    setValue('headFirstnameGenitive', inclineFirstname(headFirstname, 'genitive'), { shouldDirty: true })
                  }
                }}
                value={state.headFirstname || ""}
                required
                valid={state.headFirstname.length > 0 ? smartValidateString(state.headFirstname) && !errors.headFirstname : !errors.headFirstname}
              />
            </div>
            <div className="col-4 col-xs-12">
              {/* PATRONYMIC */}
              <TextInput
                testName={"headPatronymic"}
                testErrorName={"error-headPatronymic"}
                testCheckName={"headNoPatronymic"}
                disabled={!state.agreed || state.noPatronymic}
                lang={lang}
                content={accountForm.headPatronymicIp}
                onChange={(headPatronymic) => {
                  setValue('headPatronymic', headPatronymic, { shouldDirty: true });
                  if (state.copyContactPerson)
                    setValue('contactPersonPatronymic', headPatronymic, { shouldDirty: true });
                  if (lang === "eng") {
                    setValue('headPatronymicGenitive', headPatronymic, { shouldDirty: true });
                  } else {
                    setValue('headPatronymicGenitive', inclineMiddlename(headPatronymic, 'genitive'), { shouldDirty: true })
                  }
                }}
                checkLabel={accountForm.noPatronymic[lang]}
                checkValue={state.noPatronymic}
                checkChange={() => {
                  setValue('noPatronymic', !state.noPatronymic, { shouldDirty: true });
                  setValue('headPatronymic', "", { shouldDirty: true });
                  setValue('headPatronymicGenitive', "", { shouldDirty: true });
                }}
                required
                value={state.headPatronymic || ""}
                valid={state.headPatronymic.length > 0 ? smartValidateString(state.headPatronymic) && !errors.headPatronymic : !errors.headPatronymic}
              />
            </div>
            {lang !== "eng" ? (
              <>
                <div className="col-4 col-xs-12">
                  <TextInput
                    className={styles.grayPlaceholder}
                    testName={"headLastnameGenitive"}
                    testErrorName={"error-headLastnameGenitive"}
                    disabled={!state.agreed}
                    lang={lang}
                    content={accountForm.headLastnameGenitive}
                    onChange={(headLastnameGenitive: string) =>
                      setValue('headLastnameGenitive', headLastnameGenitive, { shouldDirty: true })
                    }
                    value={state.headLastnameGenitive || ""}
                    valid={
                      state.headLastnameGenitive.length > 0
                        ? smartValidateString(state.headLastnameGenitive) && !errors.headLastnameGenitive
                        : !errors.headLastnameGenitive
                    }
                  />
                </div>
                <div className="col-4 col-xs-12">
                  <TextInput
                    className={styles.grayPlaceholder}
                    testName={"headFirstnameGenitive"}
                    testErrorName={"error-headFirstnameGenitive"}
                    disabled={!state.agreed}
                    lang={lang}
                    content={accountForm.headFirstnameGenitive}
                    onChange={(headFirstnameGenitive: string) =>
                      setValue('headFirstnameGenitive', headFirstnameGenitive, { shouldDirty: true })
                    }
                    value={state.headFirstnameGenitive || ""}
                    valid={
                      state.headFirstnameGenitive.length > 0
                        ? smartValidateString(state.headFirstnameGenitive) && !errors.headFirstnameGenitive
                        : !errors.headFirstnameGenitive
                    }
                  />
                </div>
                <div className="col-4 col-xs-12">
                  <TextInput
                    className={styles.grayPlaceholder}
                    testName={"headPatronymicGenitive"}
                    testErrorName={"error-headPatronymicGenitive"}
                    testCheckName={"headNoPatronymic"}
                    disabled={!state.agreed || state.noPatronymic}
                    lang={lang}
                    content={accountForm.headPatronymicGenitive}
                    onChange={(headPatronymicGenitive: string) =>
                      setValue('headPatronymicGenitive', headPatronymicGenitive, { shouldDirty: true })
                    }
                    checkLabel={accountForm.noPatronymic[lang]}
                    checkValue={state.noPatronymic}
                    checkChange={() => {
                      setValue('noPatronymic', !state.noPatronymic, { shouldDirty: true });
                      setValue('headPatronymic', "", { shouldDirty: true });
                      setValue('headPatronymicGenitive', "", { shouldDirty: true });
                    }}
                    value={state.headPatronymicGenitive || ""}
                    valid={
                      state.headPatronymicGenitive.length > 0
                        ? smartValidateString(state.headPatronymicGenitive) && !errors.headPatronymicGenitive
                        : !errors.headPatronymicGenitive
                    }
                  />
                </div>
              </>
            ) : null}
          </div>
          <div className="row">
            <div className="col-4 col-xs-12">
              {/* CITIZENSHIP */}
              <SelectInput
                testName={"citizenship"}
                testErrorName={"error-citizenship"}
                noOptionsMessage={accountForm.citizenshipInput.placeholder}
                lang={lang}
                disabled={!state.agreed}
                content={accountForm.citizenshipInput}
                options={citizenshipCountriesList}
                onChange={(citizenship) => {
                  setValue('citizenship', citizenship, { shouldDirty: true });
                  setValue('passportType', getCitizenshipFormat(citizenship) === "Казахстан" ? String(passportTypeKz[0].value) : String(passportTypeRf[0].value), { shouldDirty: true });
                }}
                valid={!errors.citizenship}
                value={state.citizenship}
                required
                prefix="CitizenshipsRf"
              />
              <span className={styles.addCitizenships} onClick={() => setShowAddCitizenships(!showAddCitizenships)}>
                {accountForm.noCitizenshipsLink[lang]}
              </span>
            </div>
            <div className="col-3 col-xs-12">
              {/* BIRTH DATE */}
              <DatePicker
                testName={"birthDate"}
                testErrorName={"error-birthDate"}
                disabled={!state.agreed}
                lang={lang}
                accountForm={accountForm}
                accountFormKey="birthDate"
                onChange={(birthDate) => setValue('birthDate', birthDate, { shouldDirty: true })}
                value={state.birthDate}
                valid={
                  !errors.birthDate &&
                  validateDate({
                    current: state.birthDate,
                    min: yearsAgo(130),
                    max: yearsAgo(14),
                  })
                }
                curr={new Date(new Date().setFullYear(new Date().getFullYear() - 18))}
                min={new Date(new Date().setFullYear(new Date().getFullYear() - 85))}
                max={new Date(new Date().setFullYear(new Date().getFullYear() - 18))}
                required
              />
            </div>
            <div className="col-5 col-xs-12">
              {/* BIRTH PLACE */}
              <TextInput
                testName={"birthPlace"}
                testErrorName={"error-birthPlace"}
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.birthPlace}
                onChange={(birthPlace) => {
                  setValue('birthPlace', birthPlace, { shouldDirty: true });
                }}
                value={state.birthPlace}
                required
                valid={state.birthPlace.length > 0 ? smartValidateString(state.birthPlace) && !errors.birthPlace : !errors.birthPlace}
                onBlur={() => setFocus("")}
              />
            </div>
            {showAddCitizenships ? (
              <div className="col-12">
                <AddCitizenships
                  accountForm={accountForm}
                  lang={lang}
                  handlerChange={() => setShowAddCitizenships(false)}
                  noContacts={true}
                />
              </div>
            ) : null}
          </div>
          <div className="row">
            <div className="col-3 col-xs-12">
              <SelectInput
                testName={"passportType"}
                testErrorName={"error-passportType"}
                lang={lang}
                noOptionsMessage={accountForm.passportTypeInput.placeholder}
                options={
                  selectedCitizenship
                    ? (CITIZENSHIP_PASSPORT_TYPE_MAP[selectedCitizenship] ?? DocumentTypeValuesTypePassport)
                    : DocumentTypeValuesTypePassport
                }
                content={accountForm.passportTypeInput}
                onChange={(data) => {
                  setValue('passportType', data, { shouldDirty: true });
                }}
                value={state.passportType}
                valid={!errors.passportType && validateString(state.passportType, 300, true)}
                required
              />
            </div>
            {CitizenshipForm && <CitizenshipForm lang={lang} accountForm={accountForm} />}
            <div className="col-12 col-xs-12">
              <p className={styles.warningMessage}>
                <img src={warningImage} alt="warningImage"></img>
                {accountForm.Fiscal_Warning_Scan_Directors_Passport_KZT[lang]}
              </p>
            </div>
          </div>
        </div>
        <AddressInputs
          lang={lang}          
          accountForm={accountForm}
          addressCountries={addressCountries}
          regionCityRegistered={regionCityRegistered}
          regionCityFactual={regionCityFactual}
          regionCityResidence={regionCityResidence}
        />
      </div>
    </>
  );
}

export default IpScreenOne;
